// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { Observable, Subscription, forkJoin, of } from "rxjs";
import { catchError, finalize, switchMap, take } from "rxjs/operators";

// Custom packages
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { HelperService } from "src/app/shared/services/helper.service";
import { CondoStructuresService } from "src/app/modules/properties/condo-structures.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";

/**
 * Back-end hard limit (@see PrivacyDocumentService.MAX_PROPERTIES_PER_REQUEST):
 * a single generation request cannot target more than 50 properties.
 */
const MAX_PROPERTIES_PER_REQUEST = 50;

interface ISelectableProperty {
  id: string;
  name: string;
  addressLabel: string;
  value: boolean;
  // ID of the related CondoStructure document, needed to call the "close mapping" API
  structureId: string;
  // True when the property's mapping ("mappatura") is currently open
  mappingOpen: boolean;
  // Whether this property is checked for the bulk "close mapping" action
  closeMappingSelected: boolean;
}

/**
 * Script start
 */
@Component({
  selector: "app-generate-privacy-docs-dialog",
  templateUrl: "./generate-privacy-docs-dialog.component.html",
  styleUrls: ["./generate-privacy-docs-dialog.component.scss"]
})
export class GeneratePrivacyDocsDialogComponent implements OnInit, OnDestroy {
  properties: ISelectableProperty[] = [];
  selectAll = false;
  maxProperties = MAX_PROPERTIES_PER_REQUEST;

  // "Close mapping" feature state
  selectAllCloseMapping = false;
  closeMappingInProgress = false;

  private subscriptions: Subscription[] = [];

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<GeneratePrivacyDocsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private dialog: MatDialog,
    private helperService: HelperService,
    private condoStructuresService: CondoStructuresService,
    private propertiesService: PropertiesService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    const sourceProperties = (this.data && Array.isArray(this.data.properties)) ? this.data.properties : [];
    this.properties = sourceProperties
      .filter((item: any) => item && item._id)
      .map((item: any) => ({
        id: item._id,
        name: item.name || "",
        addressLabel: this.buildAddressLabel(item.address),
        value: false,
        structureId: item.structure || "",
        mappingOpen: item.structureOpen === true || item.structureOpen === "true",
        closeMappingSelected: false,
      }));
  }

  /**
   * Unsubscribe from every pending subscription
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Build a readable "route streetNumber, city" label from a CustomAddress-shaped object
   *
   * @since 1.0.0
   */
  private buildAddressLabel(address: any): string {
    if (!address) {
      return "";
    }
    const streetPart = [address.route, address.streetNumber].filter(Boolean).join(" ");
    return [streetPart, address.city].filter(Boolean).join(", ");
  }

  /**
   * Number of properties currently selected
   *
   * @since 1.0.0
   */
  get selectedCount(): number {
    return this.properties.filter((item) => item.value).length;
  }

  /**
   * Properties whose mapping ("mappatura") is currently open
   *
   * @since 1.0.0
   */
  get openMappingProperties(): ISelectableProperty[] {
    return this.properties.filter((item) => item.mappingOpen);
  }

  /**
   * Number of properties currently checked for the "close mapping" action
   *
   * @since 1.0.0
   */
  get closeMappingSelectedCount(): number {
    return this.properties.filter((item) => item.mappingOpen && item.closeMappingSelected).length;
  }

  /**
   * Handle "Seleziona tutti" toggle. Properties with an open mapping are excluded
   * since they cannot be selected for privacy docs generation.
   *
   * @since 1.0.0
   */
  onSelectAll(checked: boolean): void {
    this.selectAll = checked;
    this.properties.forEach((item) => {
      if (!item.mappingOpen) {
        item.value = checked;
      }
    });
  }

  /**
   * Handle "Seleziona tutte le mappature aperte" toggle
   *
   * @since 1.0.0
   */
  onSelectAllCloseMapping(checked: boolean): void {
    this.selectAllCloseMapping = checked;
    this.properties.forEach((item) => {
      if (item.mappingOpen) {
        item.closeMappingSelected = checked;
      }
    });
  }

  /**
   * Resolve the CondoStructure ID for the given property. Uses the ID already carried
   * by the (lightweight) sub-properties list when available; otherwise falls back to
   * fetching the full property document, which always has the "structure" field
   * (@see PropertyDetailPage, where the same "structure" field is used to load the
   * CondoStructure for the mapping tab).
   *
   * @since 1.0.0
   */
  private resolveStructureId(item: ISelectableProperty): Observable<string> {
    if (item.structureId) {
      return of(item.structureId);
    }
    return this.propertiesService.get(item.id).pipe(
      switchMap((res) => of((res && res.data && res.data.structure) || "")),
      catchError(() => of(""))
    );
  }

  /**
   * Handle "Chiudi la mappatura": call the close-mapping API for every selected
   * property with an open mapping (one request per CondoStructure, run in parallel).
   *
   * @since 1.0.0
   */
  onCloseMapping(): void {
    const selected = this.properties.filter((item) => item.mappingOpen && item.closeMappingSelected);
    if (!selected.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant(
          "companies.detail.privacyDocsCard.generatePropertiesDialog.confirmCloseMapping"
        ),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });

    this.subscriptions.push(
      dialogRef.beforeClosed().pipe(take(1)).subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.closeMappingInProgress = true;
        const requests = selected.map((item) =>
          this.resolveStructureId(item).pipe(
            switchMap((structureId) => {
              if (!structureId) {
                return of({
                  status: false,
                  message: this.translateService.instant(
                    "companies.detail.privacyDocsCard.generatePropertiesDialog.closeMappingMissingStructureId",
                    { name: item.name }
                  ),
                });
              }
              return this.condoStructuresService.update(structureId, { open: false }).pipe(
                catchError((err) => of({
                  status: false,
                  message: (err && err.error && err.error.message) || null,
                }))
              );
            })
          )
        );

        this.subscriptions.push(
          forkJoin(requests)
            .pipe(finalize(() => { this.closeMappingInProgress = false; }))
            .subscribe(
              (results: any[]) => {
                const failed = results.filter((res) => !res || !res.status);
                const succeededItems = selected.filter((_, index) => results[index] && results[index].status);

                if (succeededItems.length) {
                  succeededItems.forEach((item) => {
                    item.mappingOpen = false;
                    item.closeMappingSelected = false;
                  });
                  this.selectAllCloseMapping = false;
                  this.toastrService.success(
                    this.translateService.instant(
                      "companies.detail.privacyDocsCard.generatePropertiesDialog.closeMappingSuccess"
                    )
                  );
                }

                if (failed.length) {
                  failed.forEach((res) => {
                    this.toastrService.error(
                      res && res.message
                        ? res.message
                        : this.translateService.instant(
                          "companies.detail.privacyDocsCard.generatePropertiesDialog.closeMappingError"
                        )
                    );
                  });
                }
              },
              (err) => {
                this.helperService.handleError(err);
              }
            )
        );
      })
    );
  }

  /**
   * Handle "Salva": close the dialog returning the selected property ids
   *
   * @since 1.0.0
   */
  onSave(): void {
    const propertyIds = this.properties.filter((item) => item.value).map((item) => item.id);
    if (!propertyIds.length) {
      return;
    }
    if (propertyIds.length > this.maxProperties) {
      this.toastrService.error(
        this.translateService.instant(
          "companies.detail.privacyDocsCard.generatePropertiesDialog.maxPropertiesExceeded",
          { max: this.maxProperties }
        )
      );
      return;
    }
    this.dialogRef.close({ propertyIds });
  }

  /**
   * Handle "Chiudi": close the dialog without generating anything
   *
   * @since 1.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }
}
